import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PaymentForm from '../components/membership/PaymentForm';
import {
  getMembershipTypes,
  getOnboardingStatus,
  selectOnboardingPlan,
  resetOnboarding
} from '../services/api';

const deriveInitialBilling = (metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }

  const billing = metadata.billing || {};
  return {
    name: metadata.profile_snapshot?.first_name && metadata.profile_snapshot?.last_name
      ? `${metadata.profile_snapshot.first_name} ${metadata.profile_snapshot.last_name}`.trim()
      : '',
    email: metadata.profile_snapshot?.email || '',
    address: {
      line1: billing.address_line1 || '',
      city: billing.city || '',
      state: billing.state || '',
      postal_code: billing.postal_code || '',
      country: billing.country || 'US'
    }
  };
};

function Onboarding({ onStatusChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialMembership = location.state?.membership || null;
  const [membership, setMembership] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [step, setStep] = useState('plan'); // Can be 'plan', 'payment', or 'free-confirmation'
  const [selectedPlan, setSelectedPlan] = useState(null);

  const incomingSelectedPlanId = location.state?.selectedPlanId;

  const pendingPlanId = useMemo(() => {
    if (!membership) return null;

    if (membership.pending_membership_type?.id) {
      return Number(membership.pending_membership_type.id);
    }

    const metaPlanId = membership.onboarding_metadata?.selected_plan_id;
    if (metaPlanId) {
      return Number(metaPlanId);
    }

    if (incomingSelectedPlanId) {
      return Number(incomingSelectedPlanId);
    }

    return null;
  }, [membership, incomingSelectedPlanId]);

  const pendingPlan = useMemo(() => {
    if (!pendingPlanId) return null;

    if (membership?.pending_membership_type?.id) {
      return membership.pending_membership_type;
    }

    return plans.find((plan) => Number(plan.id) === pendingPlanId) || null;
  }, [membership, plans, pendingPlanId]);

  const initializeStepState = (currentMembership) => {
    if (!currentMembership) {
      setStep('plan');
      setSelectedPlan(null);
      return;
    }

    if (currentMembership.onboarding_status === 'completed') {
      // If onboarding is already completed, redirect to the originally requested page
      const redirectTo = searchParams.get('redirect') || '/dashboard';
      navigate(redirectTo, { replace: true });
      return;
    }

    if (currentMembership.onboarding_status === 'payment_pending') {
      if (currentMembership.pending_membership_type) {
        const pendingPlan = currentMembership.pending_membership_type;
        setSelectedPlan(pendingPlan);
        // Set step based on plan price - free plans show confirmation, paid plans show payment
        setStep(pendingPlan.price === 0 ? 'free-confirmation' : 'payment');
      } else {
        setStep('plan');
        setSelectedPlan(null);
      }
      return;
    }

    setStep('plan');
    setSelectedPlan(null);
  };

  const refreshMembership = async () => {
    try {
      const statusResponse = await getOnboardingStatus();
      const membershipData = statusResponse.membership || statusResponse;
      setMembership(membershipData);
      return membershipData;
    } catch (statusError) {
      console.error('Failed to refresh onboarding status:', statusError);
      if (initialMembership) {
        setMembership(initialMembership);
        return initialMembership;
      }

      setError(statusError.message || 'Unable to load onboarding status.');
      return null;
    }
  };

  useEffect(() => {
    if (step === 'payment' && !selectedPlan && pendingPlan) {
      setSelectedPlan(pendingPlan);
    }
  }, [step, selectedPlan, pendingPlan]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [plansResponse] = await Promise.all([
          getMembershipTypes()
        ]);

        const planList = plansResponse.results || plansResponse;
        setPlans(planList);

        await refreshMembership();
      } catch (loadError) {
        console.error('Failed to load onboarding resources:', loadError);
        setError('Unable to load onboarding data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track membership state changes
  useEffect(() => {
    if (membership) {
      initializeStepState(membership);
    }
  }, [membership]);

  
  const handlePlanClick = async (plan) => {
    if (processing) {
      console.log('🔒 Processing, ignoring plan click');
      return;
    }
    setError('');

    const planPrice = Number(plan.price) || 0;

    console.log('📋 Plan clicked:', {
      id: plan.id,
      name: plan.name,
      price: plan.price,
      planPrice: planPrice,
      isFree: planPrice === 0
    });

    // Always go to payment step - PaymentForm will handle free vs paid
    console.log('🔄 Setting step to payment and selected plan');
    setStep('payment');
    setSelectedPlan(plan);
  };

  const handlePaymentSuccess = async () => {
    console.log('💰 Payment success handler called');
    const updatedMembership = await refreshMembership();
    console.log('📊 Updated membership after payment:', {
      onboarding_status: updatedMembership?.onboarding_status,
      status: updatedMembership?.status,
      membership_type: updatedMembership?.membership_type?.name,
      membership_type_name: updatedMembership?.membership_type_name
    });

    if (updatedMembership) {
      // Update local state
      setMembership(updatedMembership);

      // IMPORTANT: Always call onStatusChange to update App component state
      if (onStatusChange) {
        console.log('💰 Calling onStatusChange with updated membership');
        onStatusChange(updatedMembership);
      } else {
        console.log('💰 No onStatusChange callback provided');
      }

      // Redirect to the originally requested page, or dashboard if none specified
      const redirectTo = searchParams.get('redirect') || '/dashboard';
      console.log('✅ Navigating to', redirectTo, 'after successful payment');
      navigate(redirectTo, {
        replace: true,
        state: {
          message: `Welcome! You're now a ${updatedMembership.membership_type_name || 'Member'}.`
        }
      });
    } else {
      console.error('💰 No updated membership returned from refreshMembership');
      setError('Failed to update membership status. Please try refreshing the page.');
    }
  };

  const handlePaymentFailure = async (reason) => {
    console.warn('Payment failed:', reason);
    await refreshMembership();
    setError(reason || 'Payment failed. Please try another method or choose the Community plan.');
    setStep('plan');
    setSelectedPlan(null);
  };

  const handlePlanChange = (newPlan) => {
    const planPrice = Number(newPlan.price) || 0;

    console.log('🔄 Plan change requested:', {
      id: newPlan.id,
      name: newPlan.name,
      price: newPlan.price,
      planPrice: planPrice,
      isFree: planPrice === 0
    });

    setSelectedPlan(newPlan);
    setShowPlanSelection(false);

    // Always use payment step - PaymentForm will handle free vs paid plans
    console.log('🔄 Setting step to payment for plan change');
    setStep('payment');
  };

  const renderPlanCard = (plan) => {
    const planIdNumber = Number(plan.id);
    const planPrice = Number(plan.price) || 0;
    const isPending = pendingPlanId && pendingPlanId === planIdNumber;
    return (
      <button
        key={plan.id}
        type="button"
        onClick={() => handlePlanClick(plan)}
        disabled={processing}
        className={`w-full text-left border rounded-lg p-4 transition shadow-sm ${
          isPending ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
        } ${processing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
            <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
          </div>
          <div className="text-right">
            <span className="text-xl font-semibold text-gray-900">
              {planPrice === 0 ? 'Free' : `$${planPrice.toFixed(2)}`}
            </span>
            {plan.duration_months ? (
              <p className="text-xs text-gray-500">{plan.duration_months}-month membership</p>
            ) : null}
          </div>
        </div>
        {plan.benefits && Array.isArray(plan.benefits.highlights) && plan.benefits.highlights.length > 0 && (
          <ul className="mt-3 list-disc list-inside text-sm text-gray-600">
            {plan.benefits.highlights.map((benefit, index) => (
              <li key={index}>{benefit}</li>
            ))}
          </ul>
        )}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading onboarding...</div>
        </div>
      </div>
    );
  }

  // Add a fallback for when step is not set or is invalid
  if (!step || !['plan', 'payment'].includes(step)) {
    setStep('plan');
    setSelectedPlan(null);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Preparing onboarding...</div>
        </div>
      </div>
    );
  }

  console.log('🎯 Onboarding rendering:', {
    step,
    selectedPlan: selectedPlan ? {
      id: selectedPlan.id,
      name: selectedPlan.name,
      price: selectedPlan.price,
      planPrice: Number(selectedPlan.price) || 0,
      isFree: (Number(selectedPlan.price) || 0) === 0
    } : null,
    showPlanSelection
  });

  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-md p-8 space-y-6">
        <div>
          {(() => {
            const planPrice = selectedPlan ? Number(selectedPlan.price) || 0 : 0;
            const isFree = planPrice === 0;
            return (
              <>
                <h1 className="text-3xl font-bold text-gray-900 text-center">
                  {step === 'payment' ?
                    (isFree ? 'Confirm your membership' : 'Complete your payment') :
                    'Complete your onboarding'
                  }
                </h1>
                <p className="mt-2 text-center text-sm text-gray-600">
                  {step === 'payment'
                    ? isFree
                      ? `You're almost done! Confirm your ${selectedPlan?.name || 'Community'} membership to complete setup.`
                      : `You're almost done! Provide your payment details to activate the ${selectedPlan?.name || 'Pro'} plan.`
                    : 'Choose a membership plan to finish setting up your account.'
                  }
                </p>
              </>
            );
          })()}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {step === 'plan' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Select a membership</h2>
            <p className="text-sm text-gray-600">
              You can always upgrade later if you change your mind.
            </p>
            <div className="grid grid-cols-1 gap-4">
              {plans.map(renderPlanCard)}
            </div>
          </div>
        )}

        
        {step === 'payment' && selectedPlan && (
          <div className="space-y-6">
            {/* Plan Summary with Change Option */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{selectedPlan.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedPlan.description}</p>
                  {selectedPlan.duration_months && (
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedPlan.duration_months}-month membership
                    </p>
                  )}
                </div>
                {(() => {
                    const planPrice = Number(selectedPlan.price) || 0;
                    return (
                      <div className="text-right ml-4">
                        <div className="text-2xl font-bold text-blue-600">
                          {planPrice === 0 ? 'Free' : `$${planPrice.toFixed(2)}`}
                        </div>
                        {selectedPlan.duration_months && selectedPlan.duration_months > 1 && planPrice > 0 && (
                          <p className="text-xs text-gray-500">
                            ${(planPrice / selectedPlan.duration_months).toFixed(2)}/month
                          </p>
                        )}
                      </div>
                    );
                  })()}
              </div>
              {selectedPlan.benefits && Array.isArray(selectedPlan.benefits.highlights) && selectedPlan.benefits.highlights.length > 0 && (
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">What you'll get:</h4>
                  <ul className="space-y-1">
                    {selectedPlan.benefits.highlights.slice(0, 3).map((benefit, index) => (
                      <li key={index} className="text-sm text-gray-600 flex items-center">
                        <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-blue-200">
                <button
                  type="button"
                  onClick={() => setShowPlanSelection(!showPlanSelection)}
                  className="text-sm text-blue-600 hover:text-blue-500 font-medium flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  Change Plan
                </button>
              </div>
            </div>

            {/* Plan Selection (when shown) */}
            {showPlanSelection && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Choose a different plan</h3>
                <div className="grid grid-cols-1 gap-3">
                  {plans.map((plan) => {
                    const isSelected = selectedPlan.id === plan.id;
                    const planPrice = Number(plan.price) || 0;
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => handlePlanChange(plan)}
                        className={`w-full text-left border rounded-lg p-3 transition shadow-sm ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300 bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{plan.name}</h4>
                            <p className="text-xs text-gray-600 mt-1">{plan.description}</p>
                          </div>
                          <div className="text-right">
                            <span className="font-semibold text-gray-900">
                              {planPrice === 0 ? 'Free' : `$${planPrice.toFixed(2)}`}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="border border-gray-200 rounded-lg p-4">
              <PaymentForm
                plan={selectedPlan}
                currentMembership={membership}
                onSuccess={handlePaymentSuccess}
                onError={setError}
                onFailure={handlePaymentFailure}
                autoNavigateOnSuccess={false}
                initialBilling={deriveInitialBilling(membership?.onboarding_metadata)}
              />
            </div>
          </div>
        )}

        {step === 'payment' && !selectedPlan && pendingPlanId && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              We couldn't load your selected plan automatically. Please choose your plan again to continue.
            </p>
            <div className="grid grid-cols-1 gap-4">
              {plans.map(renderPlanCard)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Onboarding;
